import { z } from "@reflag/shared/utils/zod";

import { APIEmptyResponse, APIResponse } from "./api";
import { nameSchema } from "./schemas/nameSchema";
import {
  EnvIdSchema,
  EnvironmentHeaderSchema,
  SlackChannelSchema,
  SortableCollectionSchemas,
} from "./schemas/shared";

export const EnvironmentPostSchema = z
  .object({
    name: nameSchema.max(128),
    order: z.number().min(1).optional(),
    slackChannel: SlackChannelSchema.nullish(),
    feedbackPromptSnoozePeriodSeconds: z.number().default(604800),
  })
  .strict();

export const EnvironmentPatchSchema = EnvironmentPostSchema.partial().strict();
export type EnvironmentPatchArgs = z.input<typeof EnvironmentPatchSchema>;

export type EnvironmentHeaderDTO = z.output<typeof EnvironmentHeaderSchema>;

export const EnvironmentSchema = EnvironmentHeaderSchema.extend({
  id: EnvIdSchema,
  sdkAccess: z
    .object({
      publishableKey: z.string().min(1).max(36).meta({
        description: "Publishable key",
      }),
      secretKey: z.string().min(1).max(36).meta({
        description: "Secret key",
      }),
    })
    .meta({
      description: "SDK access details",
    }),
  autoFeedbackSurveys: z
    .object({
      snoozePeriodSeconds: z.number().int().nonnegative().default(604800).meta({
        description: "Snooze period in seconds",
      }),
    })
    .meta({
      description: "Environment-specific auto feedback survey configuration",
    }),
}).meta({
  id: "environmentDetails",
});

export type EnvironmentDTO = z.output<typeof EnvironmentSchema>;

export const EnvironmentOpenApiSchema = EnvironmentSchema.omit({
  autoFeedbackSurveys: true,
}).meta({
  id: "environment",
  description: "Environment details",
});

const {
  querySchema: environmentHeaderCollectionQuerySchema,
  responseSchema: environmentHeaderCollectionSchema,
} = SortableCollectionSchemas(
  EnvironmentHeaderSchema,
  ["name", "order"],
  "order",
);

export const EnvironmentHeaderCollectionQuerySchema =
  environmentHeaderCollectionQuerySchema;

export const EnvironmentHeaderCollectionSchema =
  environmentHeaderCollectionSchema;

export const EnvironmentSelectionQuerySchema = z
  .object({
    envId: EnvIdSchema,
  })
  .strict();

export type EnvironmentSelectionQueryType = z.input<
  typeof EnvironmentSelectionQuerySchema
>;

export const RotateSDKKeyBodySchema = z
  .object({
    keyType: z.enum(["publishable", "secret"]),
  })
  .strict();

export type RotateSDKKeyBody = z.input<typeof RotateSDKKeyBodySchema>;

export const RotateSDKKeyResponseSchema = z.object({
  key: z.string(),
});

export type RotateSDKKeyResponse = z.output<typeof RotateSDKKeyResponseSchema>;

// API

export interface EnvironmentAPI {
  "/apps/:appId/environments": {
    POST: {
      body: z.input<typeof EnvironmentPostSchema>;
      response: APIResponse<z.output<typeof EnvironmentSchema>>;
      params: { appId: string };
    };
    GET: {
      response: APIResponse<z.output<typeof EnvironmentHeaderCollectionSchema>>;
      params: { appId: string };
      query: z.input<typeof EnvironmentHeaderCollectionQuerySchema>;
    };
  };
  "/apps/:appId/environments/:envId": {
    GET: {
      response: APIResponse<z.output<typeof EnvironmentSchema>>;
      params: { appId: string; envId: string };
    };
    PATCH: {
      body: z.input<typeof EnvironmentPatchSchema>;
      response: APIResponse<z.output<typeof EnvironmentSchema>>;
      params: { appId: string; envId: string };
    };
    DELETE: {
      response: APIEmptyResponse;
      params: { appId: string; envId: string };
    };
  };
  "/apps/:appId/environments/:envId/rotate-key": {
    POST: {
      body: RotateSDKKeyBody;
      response: APIResponse<RotateSDKKeyResponse>;
      params: { appId: string; envId: string };
    };
  };
}
